import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { showToast } from 'components/Toast';
import { ENV_CONFIG } from 'config/environment';
import {
  formatNumberAsCurrency,
  getUrlParams,
  getUrlSearchParamsObj,
  getUrlSearchParamsStr,
  isKeyInObject,
  roundNumberToHundredths,
  splitAmountRounded,
} from 'helpers/utils';
import {
  clearDonationSelections,
  getDonationSelections,
  getRefValue,
  getSessionID,
  setDonationSelections,
} from 'services/storage';
import { IRecipient, IRecipientIdEnum } from 'store/recipients/types';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import { IAllocationType, IPage, IPageType } from 'store/donatePage/types';
import { IEvent } from 'store/eventPage/types';
import { PaymentMethodType, OathRecurringInterval } from 'store/pay/types';

export const AUTOPILOT_CLIENT_ID = 182;
export const F4D_CLIENT_ID = 117;

export const STATES_PROJECT_REDIRECT = {
  CLIENT_ID: 118,
  PAGE_QUERY_STRINGS: ['statesproject26', 'statesproject26ads'],
  REDIRECT_URL: 'https://statesproject.org/high-five/',
} as const;

// The States Project pages redirect to TSP's own confirmation page so their
// Meta pixel can fire on their domain.
export const getIsStatesProjectRedirectPage = (clientId?: number, urlPartner?: string): boolean =>
  clientId === STATES_PROJECT_REDIRECT.CLIENT_ID &&
  STATES_PROJECT_REDIRECT.PAGE_QUERY_STRINGS.some(queryString => queryString === urlPartner);
export const MIN_DONATION_PER_CANDIDATE = 1;
export const MAX_DONATION = 1000000;
export const MIN_DONATION_AUTOPILOT = 5;
export const DONATE_SHOPPING_CART_PAGE_CATEGORY = 'donate_shopping_cart';

export const DEFAULT_ACTION_VERB = 'donate';
export const DEFAULT_ACTION_NOUN = 'donation';

export const isShoppingCartPage = (page?: IPage | IEvent | null): boolean =>
  !!page && 'page_category' in page && page.page_category === DONATE_SHOPPING_CART_PAGE_CATEGORY;

// Per-client custom action word (client_misc.action_button_alt). Verb form
// drives the donate buttons ("Donate" -> "Contribute"); noun form drives the
// checkout/confirmation copy ("donation" -> "contribution").
//
// Custom action words are gated to standard checkout flows: shopping cart pages
// always fall back to the default verbiage so the cart UI's hardcoded buttons
// stay consistent with the checkout they lead into.
export const getActionVerb = (
  page?: IPage | IEvent | null,
  fallback: string = DEFAULT_ACTION_VERB
): string =>
  isShoppingCartPage(page) ? fallback : page?.action_button_alt?.verb?.trim() || fallback;
export const getActionNoun = (
  page?: IPage | IEvent | null,
  fallback: string = DEFAULT_ACTION_NOUN
): string =>
  isShoppingCartPage(page) ? fallback : page?.action_button_alt?.noun?.trim() || fallback;

export const getMinDonationAmount = numCandidates => numCandidates * MIN_DONATION_PER_CANDIDATE;
export const getMaxDonationAmount = () => MAX_DONATION;

export const getIsTippingVariant2 = (clientId?: number) => clientId === F4D_CLIENT_ID;

export const getMaxDonationAmountContributionLimits = (selectedCandidates: IRecipient[]) => {
  if (!selectedCandidates.length) {
    // no recipients selected → no meaningful ceiling (not a $0 cap)
    return null;
  }
  const limits = selectedCandidates.map(candidate => getContributionLimitNumber(candidate));
  if (limits.some(limit => limit === null)) {
    // if any candidate has no usable contribution limit, do not limit total donation amount
    return null;
  }
  return limits.reduce<number>((maxDonation, limit) => maxDonation + (limit ?? 0), 0);
};

// The maximum donation to surface when an EVEN split's requested amount exceeds what the
// selected recipients can accept: contribution limits cap the allocation and the overage is
// dropped, so the combined limit is the effective maximum. Returns undefined when there's no
// active overage to explain. The `donationTotal >= combinedContributionLimit` clause is the
// stale-request guard — on a manual per-row edit below the cap, requestedDonationAmount stays
// higher (it isn't resynced on row edits), but there's no active overage, so suppress it.
export const getOverageMax = (
  allocationType: IAllocationType,
  requestedDonationAmount: number | undefined,
  donationTotal: number | undefined,
  combinedContributionLimit: number | null
): number | undefined =>
  allocationType === 'EVEN' &&
  requestedDonationAmount != null &&
  combinedContributionLimit != null &&
  requestedDonationAmount > combinedContributionLimit &&
  (donationTotal ?? 0) >= combinedContributionLimit
    ? combinedContributionLimit
    : undefined;

export const validateDonation = ({
  clientId,
  isRecurring = false,
  selectedCandidates,
  selectedDonationAmount,
  allCandidates,
  ticketPriceTotal,
}: {
  clientId?: number;
  isRecurring?: boolean;
  selectedCandidates: IRecipient[];
  selectedDonationAmount?: number;
  allCandidates?: CandidateType[];
  ticketPriceTotal?: number;
}): boolean => {
  const numSelectedCandidates = selectedCandidates.length;
  const maxDonationWithLimits = getMaxDonationAmountContributionLimits(selectedCandidates);
  const hasAllocatedAmounts = !!allCandidates?.length;
  const totalAllocated = roundNumberToHundredths(
    (allCandidates ?? []).reduce((sum, c) => sum + (c.donationAmount ?? 0), 0)
  );

  // Surface the first failed rule and stop, so the donor always sees exactly one
  // toast. Rule order is intentional: the amount check comes before the candidate
  // check so an otherwise-empty form shows "select a donation amount" rather than
  // stacking both errors.
  const fail = (message: string): false => {
    showToast(message, 'error');
    return false;
  };

  if (!selectedDonationAmount) {
    return fail('Please select a donation amount');
  }

  if (numSelectedCandidates <= 0) {
    return fail('Please select at least one candidate');
  }

  if (selectedDonationAmount < getMinDonationAmount(numSelectedCandidates)) {
    return fail(
      `Donation amount must be greater than ${formatNumberAsCurrency(
        MIN_DONATION_PER_CANDIDATE
      )} per candidate.`
    );
  }

  if (
    ticketPriceTotal &&
    selectedDonationAmount < ticketPriceTotal &&
    // Skip if candidate contribution limits prevent the donor from reaching ticket price
    (!maxDonationWithLimits || ticketPriceTotal <= maxDonationWithLimits)
  ) {
    return fail(
      `Donation amount must meet selected ticket amount of ${formatNumberAsCurrency(
        ticketPriceTotal
      )}`
    );
  }

  if (selectedDonationAmount > getMaxDonationAmount()) {
    return fail(`Donation amount must be less than ${formatNumberAsCurrency(MAX_DONATION)}.`);
  }

  if (maxDonationWithLimits && selectedDonationAmount > maxDonationWithLimits) {
    return fail(
      `Donation amount must be less than ${formatNumberAsCurrency(
        maxDonationWithLimits
      )} due to election contribution limits.`
    );
  }

  if (
    hasAllocatedAmounts &&
    allCandidates?.some(
      candidate =>
        candidate.donationAmount &&
        (candidate.donationAmount < 0 ||
          (candidate.donationAmount > 0 && candidate.donationAmount < MIN_DONATION_PER_CANDIDATE))
    )
  ) {
    return fail(
      `One or more recipients have invalid donation amounts. Donation amount must be at least ${formatNumberAsCurrency(
        MIN_DONATION_PER_CANDIDATE
      )} per recipient.`
    );
  }

  if (hasAllocatedAmounts && totalAllocated < selectedDonationAmount) {
    return fail(
      `Donation amount must be less than ${formatNumberAsCurrency(
        totalAllocated
      )} due to election or group contribution limits`
    );
  }

  if (
    clientId === AUTOPILOT_CLIENT_ID &&
    isRecurring &&
    selectedDonationAmount < MIN_DONATION_AUTOPILOT
  ) {
    return fail(
      `Oath on Autopilot donations must be at least ${formatNumberAsCurrency(
        MIN_DONATION_AUTOPILOT
      )}.`
    );
  }

  return true;
};

export const engageLabsCheckoutRedirect = (candidates, amount) => {
  const candidatesExternalIds = candidates.map(candidate => candidate.de_id);
  const partnerParams = getUrlParams()?.urlPartner ? `&p=${getUrlParams()?.urlPartner}` : '';
  const ref = getRefValue() ? `&ref=${getRefValue()}` : '';
  const splits = splitAmountRounded(amount, candidates.length);
  const rec = candidatesExternalIds
    .map((candidate, index) => `${candidate}:${splits[index]}`)
    .join(';');

  window.location.href = `${
    ENV_CONFIG().ENGAGE_URL
  }?rec=${rec}${ref}&s=${getSessionID()}${partnerParams}`;
};

export const getDemocracyEngineCheckoutUrl = (
  candidates,
  amount,
  monthlyKey?: string,
  searchTags?: string[]
) => {
  let partnerParams = '';
  // Check if urlPartner exists and append it to partnerParams
  const urlPartner = getUrlParams()?.urlPartner;
  if (urlPartner) {
    partnerParams = `&utm_term=${urlPartner}`;
  } else {
    // If urlPartner doesn't exist, check if searchTags exist and append each tag to partnerParams
    if (searchTags && searchTags.length > 0) {
      partnerParams += '&utm_term=search';
      searchTags.forEach((tag: string) => {
        partnerParams += `-${tag}`;
      });
    }
  }
  const ref = getUrlParams()?.urlRef ? `&sc=${getUrlParams().urlRef}` : '';
  const splits = splitAmountRounded(amount, candidates.length);
  const useCustomSplits = isKeyInObject('donationAmount', candidates[0]);

  const rec = candidates
    .map(
      (candidate, index) =>
        `${candidate.misc_ids?.ext_de_url_id}:${
          useCustomSplits ? candidate.donationAmount : splits[index]
        }`
    )
    .join(';');

  if (monthlyKey)
    return `${
      ENV_CONFIG().DONATE_URL
    }${monthlyKey}?recipients=${rec}${ref}&s=${getSessionID()}${partnerParams}`;

  return `${
    ENV_CONFIG().DEMOCRACY_ENGINE.CHECKOUT_URL
  }?recipients=${rec}${ref}&s=${getSessionID()}${partnerParams}`;
};

export const democracyEngineCheckoutRedirect = (
  candidates,
  amount,
  monthlyKey?: string,
  searchTags?: string[]
) => {
  window.location.href = getDemocracyEngineCheckoutUrl(candidates, amount, monthlyKey, searchTags);
};

export const getStripeExpressCheckoutUrl = ({
  page,
  donationAmount,
  recurringInterval,
}: {
  page: IPage;
  donationAmount: number;
  recurringInterval: OathRecurringInterval | 'one_time';
}): string => {
  const recipientIds = page.recipient_ids || [];
  const count = recipientIds.length;
  const splits = splitAmountRounded(donationAmount, count);
  const recipientsStr = recipientIds.map((id, i) => `${id}:${splits[i]}`).join(';');

  const params = new URLSearchParams({
    p: page.query_str,
    recipients: recipientsStr,
    express_checkout: 'true',
  });
  if (recurringInterval !== 'one_time') {
    params.set('recurring', 'true');
  }

  return `/donate/checkout/standard?${params.toString()}`;
};

export const getDonationSelectionsObj = () => {
  const donationSelections = getDonationSelections();
  if (!donationSelections) {
    return {};
  }
  return JSON.parse(donationSelections);
};

export const getUrlSearchParamsStrWithoutRecipients = () => {
  const searchParams = getUrlSearchParamsObj();
  searchParams.delete('recipients');
  return searchParams.toString();
};

export const getCheckoutConfirmationUrl = (pageQueryString?: string, paymentMethod?: string) => {
  const searchParams = getUrlSearchParamsObj();
  searchParams.delete('recipients');
  searchParams.delete('shopping-cart');

  if (pageQueryString) {
    searchParams.set('p', pageQueryString);
  }
  if (paymentMethod) {
    searchParams.set('m', paymentMethod);
  }

  return `${ENV_CONFIG().SITE_URL}/donate/confirmation/standard?${searchParams.toString()}`;
};

/**
 * Sibling to `getCheckoutConfirmationUrl` for the synchronous success path that
 * navigates client-side instead of following a Stripe redirect back to a `return_url`. Both need
 * `m` set the same way so the instrument survives to the confirmation page either way — see
 * `PaymentInstrument` (`helpers/mixpanelEvents.ts`) for why it can't just live in Redux.
 *
 * Mirrors `getCheckoutConfirmationUrl`'s `p` override. OATH-3005 found the raw URL's `p`
 * can go missing mid-checkout for some users, so the page-data-derived `pageQueryString` is the
 * authoritative source.
 */
export const getStandardConfirmationPath = (pageQueryString?: string, paymentMethod?: string) => {
  const searchParams = new URLSearchParams(getUrlSearchParamsStrWithoutRecipients());
  if (pageQueryString) {
    searchParams.set('p', pageQueryString);
  }
  if (paymentMethod) {
    searchParams.set('m', paymentMethod);
  }
  return `/donate/confirmation/standard?${searchParams.toString()}`;
};

export const getRecipientIdByType = (recipient: IRecipient, recipientIdType: IRecipientIdEnum) =>
  recipientIdType === IRecipientIdEnum.EXT_DE_URL_ID
    ? recipient.misc_ids?.ext_de_url_id
    : recipient[recipientIdType];

export const getSelectionsFromUrl = (
  recipients: IRecipient[],
  recipientIdType: IRecipientIdEnum
) => {
  const queryParamObj = getUrlSearchParamsObj();
  const urlRecipients = queryParamObj.get('recipients');
  const isRecurring = queryParamObj.get('recurring') === 'true';
  if (!urlRecipients) {
    return { candidates: undefined, total: 0 };
  }
  try {
    const candidates: CandidateType[] = urlRecipients
      .split(';')
      .map((recipientWithAmount, index) => {
        const [urlRecipientId, amount] = recipientWithAmount.split(':');
        const donationAmount = roundNumberToHundredths(amount);
        const recipient = recipients.find(rec => {
          const recipientId = getRecipientIdByType(rec, recipientIdType);
          return recipientId === urlRecipientId;
        });
        // Mirrors the producer-side threshold in getCustomCheckoutLink; also guards links
        // we didn't generate.
        if (!recipient || donationAmount < MIN_DONATION_PER_CANDIDATE) {
          return null;
        }
        return {
          ...recipient,
          donationAmount,
          isSelected: true,
          index,
        };
      })
      .filter(candidate => candidate !== null) as CandidateType[];
    const total = roundNumberToHundredths(
      candidates.reduce((total, candidate) => total + (candidate.donationAmount || 0), 0)
    );
    return { candidates, total, isRecurring };
  } catch {
    return { candidates: undefined, total: 0 };
  }
};

export const saveDonationSelectionsFromUrl = (recipients: IRecipient[]) => {
  const { candidates, total } = getSelectionsFromUrl(recipients, IRecipientIdEnum.EXT_DE_URL_ID);
  if (!candidates || !total) {
    return;
  }
  saveDonationSelections(candidates, total);
};

export const saveDonationSelections = (candidates: CandidateType[], amount: number) => {
  const queryParams = getUrlSearchParamsStr();
  if (!queryParams) {
    return;
  }
  const donationSelections = {
    candidates,
    total: amount.toString(),
    pathname: new URL(window.location.href).pathname,
    queryParams,
  };

  // remove sessionStorage item if it was previously set and not removed
  clearDonationSelections();

  // convert donationSelection object to string so it can be stored in sessionStorage
  const donationSelectionsString = JSON.stringify(donationSelections);
  // save donationSelections to sessionStorage after user hits donate button on partner/issue/main page
  // and BEFORE user enters login/signup flow
  setDonationSelections(donationSelectionsString);
};

export const getCandidates = (
  recipients: IRecipient[],
  savedCandidates?: CandidateType[],
  pageType?: IPageType
) =>
  recipients.map((recipient, index) => {
    const candidate =
      savedCandidates &&
      savedCandidates.find(
        savedCandidate => savedCandidate.recipient_id === recipient.recipient_id
      );
    let isSelectedDefault = !savedCandidates;
    if (pageType === 'REC') {
      isSelectedDefault = false;
    }

    return {
      ...recipient,
      index,
      isSelected: isSelectedDefault || (candidate && candidate.isSelected),
      donationAmount: !candidate ? 0 : Number(candidate.donationAmount),
    } as CandidateType;
  });

export const getCandidatesByImpactScore = (recipients: IRecipient[]) =>
  recipients.map((recipient, index) => ({
    ...recipient,
    index,
    isSelected: parseFloat(recipient.impact_score) > 5 || recipient.impact_score === null,
    donationAmount: 0,
  }));

export const getSelectedCandidates = (candidates: CandidateType[]) =>
  candidates.filter(candidate => candidate.isSelected);

/**
 * Serializes the selected candidates as the `recipient_ids` Mixpanel property:
 * `"<recipient_id>:<amount>,<recipient_id>:<amount>"`.
 */
export const getRecipientIdsWithAmounts = (candidates: CandidateType[]) =>
  getSelectedCandidates(candidates)
    .map(candidate => `${candidate.recipient_id}:${candidate.donationAmount}`)
    .join(',');

export const getSelectedCandidatesWithValidAmount = (candidates: CandidateType[]) =>
  candidates.filter(
    candidate => candidate.isSelected && candidate.donationAmount && candidate.donationAmount > 0
  );

export const sortCandidatesByContributionLimitAsc = (
  candidateA: CandidateType,
  candidateB: CandidateType
) => {
  const limitA = getContributionLimit(candidateA);
  const limitB = getContributionLimit(candidateB);

  if ((!limitA && !limitB) || limitA === limitB) {
    return 0;
  }
  if (!limitB) {
    return -1; // a before b if no limit to b
  }
  if (!limitA) {
    return 1; // b before a if no limit to a
  }
  return Number(limitA) - Number(limitB); // a before b if b bigger than a
};

export const getCandidatesWithNewAmounts = (donationTotal = 0, candidates: CandidateType[]) => {
  const selectedCandidates = getSelectedCandidates(candidates);
  // No selected recipients: nothing to allocate. Return zeroed amounts —
  // splitAmountRounded divides by 0 with no recipients.
  if (selectedCandidates.length === 0) {
    return candidates.map(candidate => ({ ...candidate, donationAmount: 0 }));
  }
  let splits = splitAmountRounded(donationTotal, selectedCandidates.length);
  let remainingTotal = donationTotal;
  let remainingCandidatesCount = selectedCandidates.length;
  const updatedCandidates: CandidateType[] = [...candidates];
  const sortedSelectedCandidates = selectedCandidates.sort(sortCandidatesByContributionLimitAsc);
  // True when an even split would push at least one recipient over its contribution
  // limit — the condition that forces a non-even, capped allocation. An all-at-limit even
  // split stays a plain even split (exceedsContributionLimit uses a strict `<`).
  const splitExceedsAnyContributionLimit = sortedSelectedCandidates.some((candidate, index) =>
    exceedsContributionLimit(candidate, splits[index])
  );
  const limitedCandidates = splitExceedsAnyContributionLimit ? sortedSelectedCandidates : [];
  const unlimitedCandidates = splitExceedsAnyContributionLimit ? [] : sortedSelectedCandidates;

  limitedCandidates.forEach(candidate => {
    const contributionLimit = getContributionLimitNumber(candidate);
    const donationAmount =
      contributionLimit !== null && contributionLimit < splits[0]
        ? contributionLimit
        : splits.shift() || 0;
    updatedCandidates[candidate.index] = {
      ...candidate,
      donationAmount,
    };
    remainingTotal = roundNumberToHundredths(remainingTotal - donationAmount);
    remainingCandidatesCount--;
    if (remainingCandidatesCount > 0) {
      splits = splitAmountRounded(remainingTotal, remainingCandidatesCount);
    }
  });

  unlimitedCandidates.forEach(candidate => {
    updatedCandidates[candidate.index] = {
      ...candidate,
      donationAmount: splits.shift(),
    };
  });

  candidates.forEach((candidate, index) => {
    if (!candidate.isSelected) {
      updatedCandidates[index].donationAmount = 0;
    }
  });

  return updatedCandidates;
};

// For EVEN splits, candidate contribution limits can cap the allocation below the requested
// amount and drop the overage (see getCandidatesWithNewAmounts). In that case the
// effective total is the sum of the allocated amounts, not what the donor typed.
// Returns the amount that should be displayed and charged.
export const reconcileEvenSplitTotal = (
  requestedAmount: number | undefined,
  allocatedCandidates: CandidateType[],
  allocationType?: IPage['allocation_type']
): number | undefined => {
  const isEvenSplit = !allocationType || allocationType === 'EVEN';
  if (!isEvenSplit || requestedAmount == null) {
    return requestedAmount;
  }
  // Sum only selected recipients so the helper is correct standalone — independent of
  // whether the caller has already zeroed unselected candidates' amounts.
  const allocatedTotal = roundNumberToHundredths(
    getSelectedCandidates(allocatedCandidates).reduce(
      (sum, candidate) => sum + (candidate.donationAmount ?? 0),
      0
    )
  );
  return allocatedTotal < requestedAmount ? allocatedTotal : requestedAmount;
};

// Allows the 1 cent spread splitAmountRounded leaves (e.g. $10 / 3 → 3.34, 3.33, 3.33).
export const isUnevenSplit = (candidates: CandidateType[]) => {
  const amounts = getSelectedCandidates(candidates).map(c => c.donationAmount ?? 0);
  if (amounts.length < 2) {
    return false;
  }
  return roundNumberToHundredths(Math.max(...amounts) - Math.min(...amounts)) > 0.01;
};

export const getShowCustomSplit = ({
  allocationType,
  candidates,
  overageMax,
}: {
  allocationType?: IAllocationType | null;
  candidates: CandidateType[];
  overageMax?: number;
}) => {
  // legacy pages may still have a NULL allocation_type. Treat as EVEN.
  if (allocationType && allocationType !== 'EVEN') {
    return true;
  }
  // overageMax covers limits capping every recipient equally: the split is even, but the
  // overage message lives in CustomSplit
  return isUnevenSplit(candidates) || overageMax !== undefined;
};

export const getProcessingRedirect = (page?: IPage | IEvent) => {
  return page?.processing_redirect || ProcessingRedirectOptions.DE_OATH;
};

export const getContributionLimit = (recipient: IRecipient) => {
  return (
    recipient.race?.election.contribution_limit ||
    recipient.misc_ids?.contribution_limit ||
    undefined
  );
};

export const getContributionLimitNumber = (candidate: IRecipient): number | null => {
  const limitStr = getContributionLimit(candidate);
  if (limitStr === undefined) {
    return null;
  }
  const limit = Number(limitStr);
  // A non-numeric contribution_limit coerces to NaN, and every comparison against NaN is
  // false — so the recipient would be silently treated as unlimited by the allocator while
  // getMaxDonationAmountContributionLimits returned NaN (which passes a `!= null` check and
  // renders nothing). Reachable: getRecipientsData stringifies limit/100, so a non-numeric
  // limit arrives here as the string "NaN". Treat it as "no usable limit" instead.
  return Number.isFinite(limit) ? limit : null;
};

// Single source of truth for "this recipient's share is over its contribution limit", so
// the allocator's cap decision and the split message can't disagree. A recipient sitting
// exactly at its limit is NOT exceeded (strict `<`).
export const exceedsContributionLimit = (candidate: IRecipient, share: number) => {
  const limit = getContributionLimitNumber(candidate);
  return limit !== null && limit < share;
};

export const getCustomCheckoutLink = (
  page: IPage | IEvent,
  candidates: CandidateType[],
  isRecurring: boolean
) => {
  const isStripe = page?.processing_redirect === ProcessingRedirectOptions.STRIPE;
  const urlPath = isStripe ? 'donate/checkout/standard' : 'checkout';
  const recipientIdType = isStripe ? IRecipientIdEnum.OATH_ID : IRecipientIdEnum.EXT_DE_URL_ID;
  // Only recipients at or above the per-recipient minimum are worth carrying:
  // getSelectionsFromUrl drops anything below it on parse.
  const recipientsStr = candidates
    .filter(can => can.isSelected && (can.donationAmount ?? 0) >= MIN_DONATION_PER_CANDIDATE)
    .map(can => `${getRecipientIdByType(can, recipientIdType)}:${can.donationAmount}`)
    .join(';');
  const urlObj = new URL(window.location.href);
  if (recipientsStr) {
    urlObj.searchParams.set('recipients', recipientsStr);
  } else {
    // Nothing allocated: drop the param rather than emit a bare `recipients=`.
    // Also clears a value inherited from the current URL, since urlObj seeds from window.location.
    urlObj.searchParams.delete('recipients');
  }
  urlObj.searchParams.set('p', page.query_str);
  if (isRecurring) {
    urlObj.searchParams.set('recurring', 'true');
  }
  const urlStr = urlObj.toString();
  const updatedUrlStr = urlStr.includes('/autopilot')
    ? urlStr.replace('/autopilot', `/${urlPath}`)
    : urlStr.replace('donate', urlPath);
  return decodeURIComponent(updatedUrlStr);
};

export const getOfflineInstructionsByPledgeMethod = (
  page?: IPage,
  pledgeMethod?: PaymentMethodType
) => {
  if (!page) {
    return [];
  }
  if (pledgeMethod === 'wire') {
    return page.offline_instructions_wire || [];
  } else if (pledgeMethod === 'check') {
    return page.offline_instructions_check || [];
  } else if (pledgeMethod === 'grant') {
    return page.offline_instructions_grant || [];
  }
  return [];
};

const setCandidatesAndSpill = (
  fixedCandidates: CandidateType[],
  flexCandidates: CandidateType[],
  spill: number
): { candidates: CandidateType[]; spill: number } => {
  const splits = splitAmountRounded(spill, flexCandidates.length);
  const newFlexCandidates: CandidateType[] = [];
  let newSpill = 0;

  flexCandidates.forEach((candidate, idx) => {
    const limitNum = getContributionLimitNumber(candidate);
    const amount = roundNumberToHundredths((candidate.donationAmount ?? 0) + splits[idx]);
    if (limitNum === null || amount <= limitNum) {
      newFlexCandidates.push({ ...candidate, donationAmount: amount });
    } else {
      fixedCandidates.push({ ...candidate, donationAmount: limitNum });
      newSpill = roundNumberToHundredths(newSpill + (amount - limitNum));
    }
  });

  if (newSpill > 0 && newFlexCandidates.length > 0) {
    return setCandidatesAndSpill(fixedCandidates, newFlexCandidates, newSpill);
  }

  return { spill: newSpill, candidates: [...fixedCandidates, ...newFlexCandidates] };
};

const distributeGroupShare = (
  groupShare: number,
  eligibleCandidates: CandidateType[]
): { candidates: CandidateType[]; spill: number } => {
  return setCandidatesAndSpill([], eligibleCandidates, groupShare);
};

export const computeDefaultSplitAllocation = (
  totalAmount = 0,
  page: Pick<IPage, 'allocation_type' | 'recipient_groups'>,
  candidates: CandidateType[]
): CandidateType[] => {
  if (!page.allocation_type || page.allocation_type === 'EVEN') {
    return getCandidatesWithNewAmounts(totalAmount, candidates);
  }

  const updatedCandidates: CandidateType[] = candidates.map(c => ({ ...c, donationAmount: 0 }));
  let spillAmount = 0;
  let remainingFunds = totalAmount;
  const sortedGroups = [...(page.recipient_groups ?? [])].sort((a, b) => a.priority - b.priority);
  let remainingWeightSum = 100;

  for (let i = 0; i < sortedGroups.length; i++) {
    const group = sortedGroups[i];
    let groupShare: number;

    if (page.allocation_type === 'WEIGHTED') {
      // spill is distributed proportionally across remaining groups, normalized by their weight
      const groupAllocationPercentage =
        remainingWeightSum > 0 ? (group.weight_percent ?? 0) / remainingWeightSum : 1;
      const spillShare = spillAmount * groupAllocationPercentage;
      groupShare = roundNumberToHundredths(
        (totalAmount * (group.weight_percent ?? 0)) / 100 + spillShare
      );
      spillAmount = roundNumberToHundredths(spillAmount - spillShare);
      remainingWeightSum = roundNumberToHundredths(
        remainingWeightSum - (group.weight_percent ?? 0)
      );
    } else {
      // spill is folded into remainingFunds; each non-last group receives min(remainingFunds, groupLimit);
      // last group (donation_limit_cents === null) absorbs all remaining funds
      if (i < sortedGroups.length - 1) {
        remainingFunds = roundNumberToHundredths(remainingFunds + spillAmount);
        spillAmount = 0;
        const groupLimitDollars = (group.donation_limit_cents ?? 0) / 100;
        groupShare = Math.min(remainingFunds, groupLimitDollars);
        remainingFunds = roundNumberToHundredths(remainingFunds - groupShare);
      } else {
        groupShare = roundNumberToHundredths(remainingFunds + spillAmount);
        spillAmount = 0;
        remainingFunds = 0;
      }
    }

    let eligibleCandidates = getSelectedCandidates(
      group.recipient_ids
        .map(id => updatedCandidates.find(c => c.recipient_id === id))
        .filter((c): c is CandidateType => !!c)
    );

    if (page.allocation_type === 'WATERFALL') {
      eligibleCandidates = eligibleCandidates.slice(0, Math.floor(groupShare));
    }

    if (eligibleCandidates.length === 0) {
      spillAmount = roundNumberToHundredths(spillAmount + groupShare);
      continue;
    }

    const { candidates: groupCandidates, spill } = distributeGroupShare(
      groupShare,
      eligibleCandidates
    );
    spillAmount = roundNumberToHundredths(spillAmount + spill);

    groupCandidates.forEach(candidate => {
      updatedCandidates[candidate.index] = {
        ...updatedCandidates[candidate.index],
        donationAmount: candidate.donationAmount ?? 0,
      };
    });
  }

  // any remaining spillAmount cannot be allocated - contribution or recipient_group limnits reached
  // validateDonation will catch the shortfall at submission time

  candidates.forEach((candidate, index) => {
    if (!candidate.isSelected) {
      updatedCandidates[index] = {
        ...updatedCandidates[index],
        donationAmount: 0,
      };
    }
  });

  return updatedCandidates;
};
